import React, { useState, useEffect, useCallback, useRef } from 'react'
import { supabase } from './supabase'
import { S, FONT_HEAD, FONT_BODY } from './styles'

const TABLE = 'agent_page_blocks'
const BLOCK_TYPES = ['featured', 'link_tile', 'link_row']

// Block images reuse the existing content-media bucket. Unique filenames mean no
// cache-buster is needed. Same jpg/png/webp allow-list as OpenHouseAdmin, 5MB cap.
const IMAGES_BUCKET = 'content-media'
const IMAGE_ALLOWED_EXTS = ['jpg', 'jpeg', 'png', 'webp']
const IMAGE_MAX_SIZE = 5 * 1024 * 1024 // 5 MB

// Shared button and chip shapes, matching Content, Events and My Wins. Gold is
// reserved for the single most important action on a screen; everything else is
// a quiet outline.
const GOLD_BTN = { background:'#fdbf00', color:'#412402', border:'none', borderRadius:'8px',
  padding:'7px 16px', fontSize:'12px', fontWeight:500, fontFamily:FONT_BODY, cursor:'pointer', whiteSpace:'nowrap' }
const QUIET_BTN = { background:'transparent', color:'#666', border:'0.5px solid #e8e8e8', borderRadius:'8px',
  padding:'7px 16px', fontSize:'12px', fontWeight:500, fontFamily:FONT_BODY, cursor:'pointer', whiteSpace:'nowrap' }
const PILL = (bg, fg) => ({ display:'inline-block', padding:'3px 8px', borderRadius:'12px', fontSize:'10px',
  fontWeight:600, fontFamily:FONT_BODY, whiteSpace:'nowrap', background:bg, color:fg })

// Reverse a public URL back to a storage path (for deletes) — mirrors
// OpenHouseAdmin's storagePathFromPublicUrl, keyed on the bucket marker.
function storagePathFromPublicUrl(url) {
  if (!url || typeof url !== 'string') return null
  const marker = `/${IMAGES_BUCKET}/`
  const idx = url.indexOf(marker)
  if (idx === -1) return null
  return url.slice(idx + marker.length).split('?')[0]
}

export default function LinksAdmin({ session, profile, showToast, navigateTo }) {
  const [targetAgent, setTargetAgent] = useState(null)

  // Readiness guard — BlocksManager reads profile.id; render nothing until
  // profile hydrates. Mirrors OpenHouseAdmin (section is open to all roles, so
  // the App.js session gate doesn't cover us).
  if (!profile) {
    return <div style={{textAlign:'center',padding:'60px',color:'#999',fontFamily:FONT_BODY}}>Loading…</div>
  }

  const isAdmin = profile.role === 'admin'

  // Admins without a target see the roster of every agent's link page. Picking
  // an agent drops into the editor below. Non-admins skip this branch entirely
  // and edit their own blocks.
  if (isAdmin && !targetAgent) {
    return (
      <AdminRoster
        session={session}
        profile={profile}
        showToast={showToast}
        onPickAgent={setTargetAgent}
      />
    )
  }

  const editingFor = targetAgent

  return (
    <div>
        <div style={{padding:'4px 0 20px'}}>
          {editingFor ? (
            <>
              <div style={{display:'flex',alignItems:'center',gap:'12px',marginBottom:'14px',flexWrap:'wrap'}}>
                <button onClick={() => setTargetAgent(null)} style={{
                  background:'transparent',
                  border:'0.5px solid #e8e8e8',
                  borderRadius:'8px',
                  padding:'6px 12px',
                  fontSize:'12px',
                  fontWeight:500,
                  fontFamily:FONT_BODY,
                  color:'#666',
                  cursor:'pointer',
                }}>← Back to roster</button>
              </div>
              <div style={{fontSize:'16px',fontWeight:500,lineHeight:1.3,color:'#1d2e42',marginBottom:'10px',fontFamily:FONT_BODY}}>Editing for <span style={{color:'#147efb'}}>{editingFor.full_name || editingFor.email}</span></div>
              <div style={{fontSize:'14px',color:'#666',fontFamily:FONT_BODY,maxWidth:'640px'}}>You are managing this agent's link page blocks. The rows stay owned by the agent and publish at <code style={{fontFamily:'monospace'}}>/links/&lt;their slug&gt;</code>.</div>
            </>
          ) : (
            <div style={{fontSize:'14px',color:'#666',fontFamily:FONT_BODY,maxWidth:'640px'}}>These blocks build your public link page. Add featured cards, link tiles, and link rows, then reorder them with the arrows.</div>
          )}
        </div>

        {/* key forces BlocksManager to remount when an admin switches between
            agents — the mount-only useEffect is what re-runs the loader. */}
        <BlocksManager key={editingFor?.id || 'self'} session={session} profile={profile} showToast={showToast} targetAgent={targetAgent} />
    </div>
  )
}

function BlocksManager({ session, profile, showToast, targetAgent }) {
  // owner = whose link page this editor is acting on. Defaults to the logged-in
  // user; when an admin opens an agent's editor via the roster, targetAgent is
  // that agent's profile (used for agent_id scoping on load/insert).
  const owner = targetAgent || profile
  const [loading, setLoading] = useState(true)
  const [blocks, setBlocks] = useState([])        // ordered by sort_order asc
  const [editingId, setEditingId] = useState(null) // null (closed) | 'new' | <id>
  const [fTitle, setFTitle] = useState('')
  const [fSubtitle, setFSubtitle] = useState('')
  const [fUrl, setFUrl] = useState('')
  const [fType, setFType] = useState(BLOCK_TYPES[0])
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [confirmDeleteId, setConfirmDeleteId] = useState(null)
  const [busy, setBusy] = useState(false)          // delete/reorder in flight
  const [fImageUrl, setFImageUrl] = useState('')
  const [fImageOnly, setFImageOnly] = useState(false)
  const [uploading, setUploading] = useState(false)
  const fileInputRef = useRef(null)
  const [ownerSlug, setOwnerSlug] = useState(null)        // owner's profiles.slug
  const [ownerPublished, setOwnerPublished] = useState(true)
  const [ownerBrokerage, setOwnerBrokerage] = useState('')
  const [ownerLicense, setOwnerLicense] = useState('')
  const [publishSaving, setPublishSaving] = useState(false)
  const [ownerShowCalc, setOwnerShowCalc] = useState(false)
  const [calcSaving, setCalcSaving] = useState(false)

  const loadBlocks = useCallback(async () => {
    setLoading(true)
    // Blocks + the owner's slug/publish flag in parallel. The profile fields
    // come from the profiles row (targetAgent from the roster doesn't carry them).
    const [blocksRes, profRes] = await Promise.all([
      supabase.from(TABLE).select('*').eq('agent_id', owner.id).order('sort_order', { ascending: true }),
      supabase.from('profiles').select('slug, page_published, brokerage, show_calculator, license_number').eq('id', owner.id).single(),
    ])
    if (blocksRes.error) {
      console.error('[agent_page_blocks load]', blocksRes.error)
      showToast('Could not load link blocks: ' + blocksRes.error.message)
      setBlocks([])
      setLoading(false)
      return
    }
    setBlocks(blocksRes.data || [])
    if (profRes.error) {
      // Non-fatal — blocks still load; the URL bar falls back to the not-set-up note.
      console.error('[profile slug load]', profRes.error)
      setOwnerSlug(null)
      setOwnerPublished(false)
      setOwnerBrokerage('')
      setOwnerShowCalc(false)
      setOwnerLicense('')
    } else {
      setOwnerSlug(profRes.data?.slug || null)
      setOwnerPublished(profRes.data?.page_published === true)
      setOwnerBrokerage(profRes.data?.brokerage || '')
      setOwnerShowCalc(profRes.data?.show_calculator === true)
      setOwnerLicense(profRes.data?.license_number || '')
    }
    setLoading(false)
  }, [showToast, owner.id])

  // Fire on mount only. Depending on `loadBlocks` here would re-fire any time
  // its `showToast` dep changes ref — and App.js's showToast is a fresh
  // function on every Dashboard render — wiping a half-typed block entry.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadBlocks() }, [])

  function openNew() {
    setEditingId('new')
    setFTitle('')
    setFSubtitle('')
    setFUrl('')
    setFType(BLOCK_TYPES[0])
    setFImageUrl('')
    setFImageOnly(false)
    setError('')
  }
  function openEdit(b) {
    setEditingId(b.id)
    setFTitle(b.title || '')
    setFSubtitle(b.subtitle || '')
    setFUrl(b.url || '')
    setFType(BLOCK_TYPES.includes(b.block_type) ? b.block_type : BLOCK_TYPES[0])
    setFImageUrl(b.image_url || '')
    setFImageOnly(!!b.image_only)
    setError('')
  }
  function closeForm() {
    setEditingId(null)
    setError('')
  }

  function validate() {
    if (!fTitle.trim()) return 'Title is required'
    if (!fUrl.trim()) return 'URL is required'
    if (!fUrl.trim().toLowerCase().startsWith('https://')) return 'URL must start with https://'
    return ''
  }

  async function handleSave() {
    const v = validate()
    if (v) { setError(v); return }
    setError('')
    setSaving(true)
    try {
      const payload = {
        title: fTitle.trim(),
        subtitle: fSubtitle.trim() || null,
        url: fUrl.trim(),
        block_type: fType,
        image_url: fImageUrl || null,
        image_only: (fType === 'featured' && !!fImageUrl && fImageOnly),
        updated_at: new Date().toISOString(),
      }
      if (editingId && editingId !== 'new') {
        // block_type/title/subtitle/url are the only editable fields; agent_id,
        // sort_order and is_active are left untouched on update.
        const { error: upErr } = await supabase.from(TABLE).update(payload).eq('id', editingId)
        if (upErr) throw upErr
        showToast('Block updated')
      } else {
        // New row goes to the bottom: current max sort_order + 1.
        const nextOrder = blocks.length ? Math.max(...blocks.map(b => b.sort_order ?? 0)) + 1 : 0
        payload.agent_id = owner.id
        payload.sort_order = nextOrder
        payload.is_active = true
        const { error: insErr } = await supabase.from(TABLE).insert(payload)
        if (insErr) throw insErr
        showToast('Block added')
      }
      closeForm()
      await loadBlocks()
    } catch (e) {
      console.error('[agent_page_blocks save]', e)
      showToast('Save failed: ' + (e.message || 'unknown'))
    } finally {
      setSaving(false)
    }
  }

  async function handleDelete(id) {
    setBusy(true)
    try {
      const blk = blocks.find(x => x.id === id)
      const { error: delErr } = await supabase.from(TABLE).delete().eq('id', id)
      if (delErr) throw delErr
      // Best-effort: remove the block's image from storage after the row is gone.
      if (blk && blk.image_url) {
        const path = storagePathFromPublicUrl(blk.image_url)
        if (path) {
          const { error: rmErr } = await supabase.storage.from(IMAGES_BUCKET).remove([path])
          if (rmErr) console.warn('[block image remove]', rmErr)
        }
      }
      setConfirmDeleteId(null)
      if (editingId === id) closeForm()
      showToast('Block deleted')
      await loadBlocks()
    } catch (e) {
      console.error('[agent_page_blocks delete]', e)
      showToast('Delete failed: ' + (e.message || 'unknown'))
    } finally {
      setBusy(false)
    }
  }

  async function moveBlock(idx, direction) {
    const target = idx + direction
    if (target < 0 || target >= blocks.length) return
    const a = blocks[idx]
    const b = blocks[target]
    setBusy(true)
    try {
      const now = new Date().toISOString()
      // Swap the two rows' sort_order values.
      const { error: e1 } = await supabase.from(TABLE).update({ sort_order: b.sort_order, updated_at: now }).eq('id', a.id)
      if (e1) throw e1
      const { error: e2 } = await supabase.from(TABLE).update({ sort_order: a.sort_order, updated_at: now }).eq('id', b.id)
      if (e2) throw e2
      await loadBlocks()
    } catch (e) {
      console.error('[agent_page_blocks move]', e)
      showToast('Reorder failed: ' + (e.message || 'unknown'))
    } finally {
      setBusy(false)
    }
  }

  async function uploadImage(fileList) {
    const file = fileList && fileList[0]
    if (!file) return
    const ext = (file.name.split('.').pop() || '').toLowerCase()
    if (!IMAGE_ALLOWED_EXTS.includes(ext)) { showToast('Image must be jpg, png, or webp'); return }
    if (file.size > IMAGE_MAX_SIZE) { showToast('Image exceeds 5MB'); return }
    setUploading(true)
    try {
      // Keyed on agent_id (not the row id) so uploads work before the row exists.
      const filename = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`
      const path = `block-images/${owner.id}/${filename}`
      const { error: upErr } = await supabase.storage.from(IMAGES_BUCKET).upload(path, file, {
        upsert: false,
        contentType: file.type || `image/${ext === 'jpg' ? 'jpeg' : ext}`,
      })
      if (upErr) { showToast('Upload failed: ' + upErr.message); return }
      const { data: urlData } = supabase.storage.from(IMAGES_BUCKET).getPublicUrl(path)
      if (urlData?.publicUrl) { setFImageUrl(urlData.publicUrl); showToast('Image uploaded') }
    } finally {
      setUploading(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  async function removeImage() {
    if (!fImageUrl) return
    const path = storagePathFromPublicUrl(fImageUrl)
    // Mirror OpenHouseAdmin's delete path: reverse the URL and remove the file.
    if (path) {
      const { error: rmErr } = await supabase.storage.from(IMAGES_BUCKET).remove([path])
      if (rmErr) console.warn('[block image remove]', rmErr)
    }
    // If editing an existing row, clear image_url now so a later Cancel can't
    // leave the row pointing at a deleted file.
    if (editingId && editingId !== 'new') {
      const { error: upErr } = await supabase.from(TABLE).update({ image_url: null, updated_at: new Date().toISOString() }).eq('id', editingId)
      if (upErr) { console.error('[block image clear]', upErr); showToast('Could not clear image: ' + upErr.message) }
      else await loadBlocks()
    }
    setFImageUrl('')
  }

  const liveUrl = ownerSlug ? `https://truetopproducer.com/links/${ownerSlug}` : null
  // Gates the public RPC enforces besides page_published: a slug and a brokerage
  // must both exist or the page can't render. Don't let an agent publish otherwise.
  const hasBrokerage = !!(ownerBrokerage && String(ownerBrokerage).trim())
  const hasLicense = !!(ownerLicense && String(ownerLicense).trim())
  const canPublish = !!ownerSlug && hasBrokerage && hasLicense

  async function copyUrl() {
    if (!liveUrl || !ownerPublished) return
    try {
      await navigator.clipboard.writeText(liveUrl)
      showToast('Copied to clipboard')
    } catch { showToast('Copy failed — try selecting manually') }
  }

  async function togglePublish() {
    if (publishSaving) return
    const next = !ownerPublished
    // Unpublishing is always allowed; publishing requires the render gates.
    if (next && !canPublish) return
    setPublishSaving(true)
    const { error: upErr } = await supabase.from('profiles').update({ page_published: next }).eq('id', owner.id)
    if (upErr) {
      console.error('[page_published update]', upErr)
      showToast('Could not update publish status: ' + upErr.message)
      setPublishSaving(false)
      return
    }
    setOwnerPublished(next)
    showToast(next ? 'Link page published' : 'Link page unpublished')
    setPublishSaving(false)
  }

  async function toggleCalculator() {
    if (calcSaving) return
    const next = !ownerShowCalc
    setCalcSaving(true)
    const { error: upErr } = await supabase.from('profiles').update({ show_calculator: next }).eq('id', owner.id)
    if (upErr) {
      console.error('[show_calculator update]', upErr)
      showToast('Could not update calculator setting: ' + upErr.message)
      setCalcSaving(false)
      return
    }
    setOwnerShowCalc(next)
    showToast(next ? 'Payment calculator shown' : 'Payment calculator hidden')
    setCalcSaving(false)
  }

  if (loading) return <div style={{textAlign:'center',padding:'60px',color:'#999',fontFamily:FONT_BODY}}>Loading…</div>

  const formOpen = editingId !== null
  const isEditingExisting = formOpen && editingId !== 'new'

  return (
    <div style={{paddingBottom:'80px'}}>
      {/* Share / copy URL bar */}
      <div style={{background:'white',border:'0.5px solid #e8e8e8',borderRadius:'12px',padding:'16px 18px',marginBottom:'16px'}}>
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:'12px',flexWrap:'wrap',marginBottom:'10px'}}>
          <div style={{fontSize:'10px',fontWeight:700,letterSpacing:'1.5px',color:'#999',fontFamily:FONT_HEAD}}>YOUR LINK PAGE</div>
          {liveUrl && (
            <div style={{display:'flex',alignItems:'center',gap:'10px'}}>
              <span style={{fontSize:'12px',fontWeight:600,color: ownerPublished ? '#147efb' : '#999',fontFamily:FONT_BODY}}>{ownerPublished ? 'Published' : 'Not published'}</span>
              <button
                onClick={togglePublish}
                disabled={publishSaving || (!ownerPublished && !canPublish)}
                role="switch"
                aria-checked={ownerPublished}
                aria-label="Publish link page"
                title={(!ownerPublished && !canPublish) ? 'Complete your profile before publishing' : (ownerPublished ? 'Unpublish' : 'Publish')}
                style={{
                  width:'44px',height:'24px',borderRadius:'999px',border:'none',position:'relative',flexShrink:0,
                  background: ownerPublished ? '#147efb' : '#e8e8e8',
                  cursor: (publishSaving || (!ownerPublished && !canPublish)) ? 'not-allowed' : 'pointer',
                  opacity: (!ownerPublished && !canPublish) ? 0.5 : 1,
                  transition:'background 0.15s',
                }}
              >
                <span style={{position:'absolute',top:'3px',left: ownerPublished ? '23px' : '3px',width:'18px',height:'18px',borderRadius:'50%',background:'white',transition:'left 0.15s'}} />
              </button>
            </div>
          )}
        </div>
        {!liveUrl ? (
          <div style={{fontSize:'13px',color:'#999',fontFamily:FONT_BODY}}>This link page isn't set up yet — no page URL has been assigned.</div>
        ) : (
          <>
            <div style={{display:'flex',alignItems:'center',gap:'10px',flexWrap:'wrap'}}>
              <div title={liveUrl} style={{flex:1,minWidth:'200px',fontSize:'13px',fontFamily:FONT_BODY,color: ownerPublished ? '#147efb' : '#999',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{liveUrl}</div>
              <button onClick={copyUrl} disabled={!ownerPublished} style={{...S.btnCopy,flex:'none',padding:'9px 18px',cursor: ownerPublished ? 'pointer' : 'not-allowed',opacity: ownerPublished ? 1 : 0.5}}>Copy</button>
            </div>
            {!ownerPublished && (
              <div style={{fontSize:'12px',color:'#633806',marginTop:'8px',fontFamily:FONT_BODY}}>
                {!hasBrokerage
                  ? 'Set your brokerage on your profile before you can publish this page.'
                  : !hasLicense
                    ? 'Add your license number on your profile before you can publish this page.'
                    : 'This page isn\'t live yet — turn on Publish to make it public.'}
              </div>
            )}
          </>
        )}
        {/* Payment calculator opt-in — no gates, independent of publish state. */}
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:'12px',flexWrap:'wrap',borderTop:'0.5px solid #e8e8e8',marginTop:'14px',paddingTop:'14px'}}>
          <span style={{fontSize:'13px',color:'#1d2e42',fontFamily:FONT_BODY}}>Show payment calculator</span>
          <button
            onClick={toggleCalculator}
            disabled={calcSaving}
            role="switch"
            aria-checked={ownerShowCalc}
            aria-label="Show payment calculator"
            title={ownerShowCalc ? 'Hide calculator' : 'Show calculator'}
            style={{
              width:'44px',height:'24px',borderRadius:'999px',border:'none',position:'relative',flexShrink:0,
              background: ownerShowCalc ? '#147efb' : '#e8e8e8',
              cursor: calcSaving ? 'not-allowed' : 'pointer',
              transition:'background 0.15s',
            }}
          >
            <span style={{position:'absolute',top:'3px',left: ownerShowCalc ? '23px' : '3px',width:'18px',height:'18px',borderRadius:'50%',background:'white',transition:'left 0.15s'}} />
          </button>
        </div>
      </div>

      {/* Add / edit form */}
      <div style={{background:'white',border:'0.5px solid #e8e8e8',borderRadius:'12px',padding:'20px',marginBottom:'20px'}}>
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:'12px',flexWrap:'wrap',marginBottom: formOpen ? '18px' : '0'}}>
          <div style={{fontSize:'16px',fontWeight:500,color:'#1d2e42',fontFamily:FONT_BODY}}>{isEditingExisting ? 'Edit block' : 'Add block'}</div>
          {!formOpen && (
            <button onClick={openNew} style={QUIET_BTN}>+ Add block</button>
          )}
        </div>

        {formOpen && (
          <>
            {error && <div style={S.errorBox}>{error}</div>}

            <div style={S.field}>
              <label style={S.label}>TITLE *</label>
              <input value={fTitle} onChange={e=>setFTitle(e.target.value)} style={S.input} placeholder="Featured listing" />
            </div>
            <div style={S.field}>
              <label style={S.label}>SUBTITLE</label>
              <input value={fSubtitle} onChange={e=>setFSubtitle(e.target.value)} style={S.input} placeholder="Optional supporting line" />
            </div>
            <div style={S.field}>
              <label style={S.label}>URL *</label>
              <input value={fUrl} onChange={e=>setFUrl(e.target.value)} style={S.input} placeholder="https://…" />
            </div>
            <div style={S.field}>
              <label style={S.label}>BLOCK TYPE *</label>
              <select value={fType} onChange={e=>setFType(e.target.value)} style={S.input}>
                {BLOCK_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div style={S.field}>
              <label style={S.label}>IMAGE</label>
              {fImageUrl ? (
                <div style={{display:'flex',alignItems:'center',gap:'12px'}}>
                  <img src={fImageUrl} alt="" style={{width:'64px',height:'64px',borderRadius:'8px',objectFit:'cover',border:'1px solid #e8e8e8',flexShrink:0}} />
                  <button type="button" onClick={removeImage} disabled={uploading} style={{background:'transparent',border:'1px solid rgba(200,16,46,0.3)',color:'#C8102E',borderRadius:'6px',padding:'6px 12px',fontSize:'12px',fontWeight:600,cursor:uploading?'wait':'pointer',fontFamily:FONT_HEAD,letterSpacing:'0.5px'}}>Remove</button>
                </div>
              ) : (
                <>
                  <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp" style={{display:'none'}} onChange={e=>uploadImage(e.target.files)} />
                  <button type="button" onClick={()=>!uploading && fileInputRef.current?.click()} disabled={uploading} style={{...QUIET_BTN,cursor:uploading?'wait':'pointer',opacity:uploading?0.6:1}}>{uploading ? 'Uploading…' : 'Upload image'}</button>
                </>
              )}
              <div style={{fontSize:'12px',color:'#999',marginTop:'8px',fontFamily:FONT_BODY}}>Recommended: 1080 x 405 banner (8:3), under 5MB</div>
              {fType === 'link_row' && (
                <div style={{fontSize:'12px',color:'#999',marginTop:'8px',fontFamily:FONT_BODY}}>Link row blocks always show an icon — this image won't be displayed on the public page.</div>
              )}
            </div>
            {fType === 'featured' && fImageUrl && (
              <div style={S.field}>
                <label style={{display:'flex',alignItems:'center',gap:'8px',cursor:'pointer',fontFamily:FONT_BODY,fontSize:'13px',color:'#666'}}>
                  <input type="checkbox" checked={fImageOnly} onChange={e=>setFImageOnly(e.target.checked)} />
                  Image only (hide title, subtitle, and link — the whole card is the image)
                </label>
              </div>
            )}

            <div style={{display:'flex',gap:'10px',marginTop:'24px'}}>
              <button onClick={handleSave} disabled={saving} style={{...GOLD_BTN,opacity:saving?0.6:1,cursor:saving?'wait':'pointer'}}>{saving ? 'Saving…' : (isEditingExisting ? 'Save changes' : 'Add block')}</button>
              <button onClick={closeForm} disabled={saving} style={QUIET_BTN}>Cancel</button>
            </div>
          </>
        )}
      </div>

      {/* Blocks list */}
      {blocks.length === 0 ? (
        <div style={{background:'white',border:'0.5px solid #e8e8e8',borderRadius:'12px',padding:'40px 24px',textAlign:'center',color:'#999',fontSize:'12.5px',fontFamily:FONT_BODY}}>
          No blocks yet. Click “+ Add Block” to create the first one.
        </div>
      ) : (
        <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
          {blocks.map((b, idx) => (
            <div key={b.id} style={{background:'white',border:'0.5px solid #e8e8e8',borderRadius:'12px',padding:'14px 16px',display:'flex',alignItems:'center',gap:'14px'}}>
              {/* reorder */}
              <div style={{display:'flex',flexDirection:'column',gap:'4px',flexShrink:0}}>
                <button onClick={()=>moveBlock(idx,-1)} disabled={idx===0||busy} aria-label="Move up" style={{width:'28px',height:'24px',borderRadius:'5px',border:'1px solid #e8e8e8',background:'#f4f4f2',color:'#666',cursor:(idx===0||busy)?'not-allowed':'pointer',opacity:idx===0?0.4:1,fontSize:'12px'}}>↑</button>
                <button onClick={()=>moveBlock(idx,1)} disabled={idx===blocks.length-1||busy} aria-label="Move down" style={{width:'28px',height:'24px',borderRadius:'5px',border:'1px solid #e8e8e8',background:'#f4f4f2',color:'#666',cursor:(idx===blocks.length-1||busy)?'not-allowed':'pointer',opacity:idx===blocks.length-1?0.4:1,fontSize:'12px'}}>↓</button>
              </div>

              {/* thumbnail */}
              {b.image_url && <img src={b.image_url} alt="" style={{width:'44px',height:'44px',borderRadius:'8px',objectFit:'cover',flexShrink:0,border:'1px solid #e8e8e8'}} />}

              {/* content */}
              <div style={{flex:1,minWidth:0}}>
                <div style={{display:'flex',alignItems:'center',gap:'8px',marginBottom:'3px',flexWrap:'wrap'}}>
                  <span style={{fontSize:'14px',fontWeight:500,color:'#1d2e42',fontFamily:FONT_BODY}}>{b.title || <span style={{color:'#999',fontStyle:'italic'}}>(no title)</span>}</span>
                  <span style={PILL('#E4EDF7','#123763')}>{b.block_type}</span>
                  {b.is_active === false && <span style={PILL('#F4F4F2','#666')}>Hidden</span>}
                </div>
                {b.subtitle && <div style={{fontSize:'13px',color:'#666',fontFamily:FONT_BODY,marginBottom:'2px'}}>{b.subtitle}</div>}
                {b.url && <a href={b.url} target="_blank" rel="noopener noreferrer" title={b.url} style={{display:'block',fontSize:'12px',color:'#147efb',textDecoration:'none',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis',fontFamily:FONT_BODY}}>{b.url}</a>}
              </div>

              {/* actions */}
              <div style={{display:'flex',alignItems:'center',gap:'8px',flexShrink:0}}>
                {confirmDeleteId === b.id ? (
                  <>
                    <span style={{fontSize:'12px',color:'#C8102E',fontFamily:FONT_BODY,whiteSpace:'nowrap'}}>Delete?</span>
                    <button onClick={()=>handleDelete(b.id)} disabled={busy} style={{background:'#C8102E',color:'white',border:'none',borderRadius:'6px',padding:'5px 12px',fontSize:'11px',fontWeight:600,fontFamily:FONT_BODY,cursor:busy?'wait':'pointer',opacity:busy?0.6:1}}>Yes</button>
                    <button onClick={()=>setConfirmDeleteId(null)} disabled={busy} style={{background:'transparent',border:'0.5px solid #e8e8e8',borderRadius:'6px',padding:'5px 12px',fontSize:'11px',fontWeight:500,color:'#666',fontFamily:FONT_BODY,cursor:'pointer'}}>No</button>
                  </>
                ) : (
                  <>
                    <button onClick={()=>openEdit(b)} style={{background:'none',border:'none',padding:0,color:'#147efb',fontSize:'11.5px',fontWeight:600,fontFamily:FONT_BODY,cursor:'pointer'}}>Edit</button>
                    <button onClick={()=>setConfirmDeleteId(b.id)} style={{padding:'5px 12px',borderRadius:'6px',border:'0.5px solid rgba(200,16,46,0.3)',background:'transparent',color:'#C8102E',fontSize:'11px',fontWeight:500,fontFamily:FONT_BODY,cursor:'pointer'}}>Delete</button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ────────────────────────────────────────────────────────────────────────────
// Admin-only roster: one row per agent that has link blocks, plus a
// "+ New for agent" picker that drops the admin into the editor for the chosen
// agent (including agents with no blocks yet). Mirrors OpenHouseAdmin's roster.
// ────────────────────────────────────────────────────────────────────────────

const LINKS_ROSTER_COLS = '1.6fr 1.6fr 0.7fr 0.7fr'
// Four columns need less room than the open house roster's seven, but the same
// clipping applied: below this the card scrolls rather than cutting Actions off.
const LINKS_ROSTER_MIN_WIDTH = '560px'

function AdminRoster({ session, profile, showToast, onPickAgent }) {
  const [loading, setLoading] = useState(true)
  const [rows, setRows] = useState([])          // { agent, count }, one per agent with blocks
  const [agentsById, setAgentsById] = useState({})
  const [agents, setAgents] = useState([])      // sorted, selectable
  const [pickerOpen, setPickerOpen] = useState(false)
  const [pickerAgentId, setPickerAgentId] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [blocksRes, agentsRes] = await Promise.all([
        supabase.from(TABLE).select('agent_id'),
        supabase.from('profiles').select('id, full_name, email, role, brokerage').in('role', ['agent', 'admin']).order('full_name'),
      ])
      if (blocksRes.error) {
        console.error('[agent_page_blocks roster]', blocksRes.error)
        showToast('Could not load link blocks: ' + blocksRes.error.message)
      }
      if (agentsRes.error) {
        console.error('[agents roster]', agentsRes.error)
        showToast('Could not load agents: ' + agentsRes.error.message)
      }

      const rawAgents = agentsRes.data || []
      const byId = {}
      rawAgents.forEach(a => { byId[a.id] = a })

      // Count blocks per agent.
      const counts = {}
      for (const r of (blocksRes.data || [])) {
        if (!r.agent_id) continue
        counts[r.agent_id] = (counts[r.agent_id] || 0) + 1
      }

      const sortedAgents = [...rawAgents].sort((a, b) => String(a.full_name || '').localeCompare(String(b.full_name || '')))

      // One roster row per agent (all agents + admins), block count including zero.
      const rosterRows = sortedAgents.map(a => ({ agentId: a.id, agent: a, count: counts[a.id] || 0 }))

      setRows(rosterRows)
      setAgentsById(byId)
      setAgents(sortedAgents)
    } finally {
      setLoading(false)
    }
  }, [showToast])

  // Fire on mount only — same pattern as BlocksManager. Including `load` in deps
  // would re-fire whenever showToast's identity changes (every parent render).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load() }, [])

  function handlePick() {
    const agent = agentsById[pickerAgentId]
    if (!agent) return
    setPickerOpen(false)
    setPickerAgentId('')
    onPickAgent(agent)
  }

  return (
    <div>
        <div style={{padding:'4px 0 20px'}}>
          <div style={{fontSize:'14px',color:'#666',fontFamily:FONT_BODY,maxWidth:'640px'}}>One row per agent, with their block count. Click Edit to manage a link page on their behalf — including agents who don't have any blocks yet.</div>
        </div>

        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:'12px',flexWrap:'wrap',marginBottom:'16px'}}>
          <div style={{fontSize:'12px',color:'#666',fontFamily:FONT_BODY}}>
            {loading ? 'Loading…' : `${rows.length} agent${rows.length === 1 ? '' : 's'}`}
          </div>
          {!pickerOpen && (
            <button onClick={() => setPickerOpen(true)} style={GOLD_BTN}>+ New for agent</button>
          )}
        </div>

        {pickerOpen && (
          <div style={{
            background:'white',
            border:'1px solid #e8e8e8',
            borderRadius:'12px',
            padding:'20px 22px',
            marginBottom:'20px',
            display:'flex',
            flexDirection:'column',
            gap:'12px',
            maxWidth:'560px',
          }}>
            <div style={{fontSize:'10px',fontWeight:700,fontFamily:FONT_HEAD,color:'#999',letterSpacing:'1.5px'}}>MANAGE LINK PAGE FOR AGENT</div>
            <select
              value={pickerAgentId}
              onChange={e => setPickerAgentId(e.target.value)}
              style={{
                font:'inherit',
                height:'40px',
                padding:'0 12px',
                border:'1.5px solid #e8e8e8',
                borderRadius:'8px',
                background:'white',
                color:'#1d2e42',
                boxSizing:'border-box',
                outline:'none',
              }}
            >
              <option value="">— Select agent —</option>
              {agents.map(a => {
                const baseName = a.full_name || a.email || '(no name)'
                const label = a.brokerage && String(a.brokerage).trim() ? `${baseName} · ${a.brokerage}` : baseName
                return <option key={a.id} value={a.id}>{label}</option>
              })}
            </select>
            <div style={{display:'flex',gap:'10px',marginTop:'4px'}}>
              <button
                onClick={handlePick}
                disabled={!pickerAgentId}
                style={{...GOLD_BTN, opacity: pickerAgentId ? 1 : 0.45, cursor: pickerAgentId ? 'pointer' : 'not-allowed'}}
              >Open editor for agent</button>
              <button
                onClick={() => { setPickerOpen(false); setPickerAgentId('') }}
                style={QUIET_BTN}
              >Cancel</button>
            </div>
          </div>
        )}

        {loading ? (
          <div style={{padding:'40px 0',color:'#666',fontFamily:FONT_BODY}}>Loading link pages…</div>
        ) : rows.length === 0 ? (
          <div style={{
            padding:'48px 24px',
            textAlign:'center',
            background:'#f4f4f2',
            border:'1px dashed #e8e8e8',
            borderRadius:'12px',
            color:'#666',
            fontFamily:FONT_BODY,
          }}>
            <div style={{fontSize:'14px',fontWeight:500,color:'#1d2e42',marginBottom:'8px',fontFamily:FONT_BODY}}>No agents found</div>
            <div style={{fontSize:'13px'}}>No agent or admin profiles are available to manage.</div>
          </div>
        ) : (
          <div style={{background:'white',border:'1px solid #e8e8e8',borderRadius:'12px',overflowX:'auto',overflowY:'hidden',WebkitOverflowScrolling:'touch'}}>
            {/* Header and rows share this wrapper so one scrollbar governs both
                and their columns stay in register. */}
            <div style={{minWidth: LINKS_ROSTER_MIN_WIDTH}}>
            <div style={{
              display:'grid',
              gridTemplateColumns: LINKS_ROSTER_COLS,
              gap:'12px',
              padding:'12px 16px',
              background:'#f4f4f2',
              borderBottom:'1px solid #e8e8e8',
              fontSize:'10px',
              fontWeight:600,
              letterSpacing:'1px',
              color:'#666',
              fontFamily:FONT_BODY,
              textTransform:'uppercase',
            }}>
              <div>Agent</div>
              <div>Brokerage</div>
              <div>Blocks</div>
              <div>Actions</div>
            </div>
            {rows.map(r => (
              <div key={r.agentId} style={{
                display:'grid',
                gridTemplateColumns: LINKS_ROSTER_COLS,
                gap:'12px',
                padding:'14px 16px',
                borderBottom:'1px solid #e8e8e8',
                fontSize:'13px',
                fontFamily:FONT_BODY,
                color:'#1d2e42',
                alignItems:'center',
              }}>
                <div style={{fontWeight:700}}>{r.agent.full_name || r.agent.email || '—'}</div>
                <div style={{color:'#666'}}>{r.agent.brokerage || '—'}</div>
                <div style={{color:'#666'}}>{r.count}</div>
                <div>
                  <button onClick={() => onPickAgent(r.agent)} style={{
                    background:'none', border:'none', padding:0,
                    color:'#147efb', fontSize:'11.5px', fontWeight:600,
                    fontFamily:FONT_BODY, cursor:'pointer',
                  }}>Edit</button>
                </div>
              </div>
            ))}
            </div>
          </div>
        )}
    </div>
  )
}
